/**
 * Juice Item Specifications — item -> lot -> load.
 *
 * The item row carries a column per banded measure: the average over lots, with
 * that measure's range beneath it. USDA colour is not a column — it has no
 * limits anywhere in Spec Management — but it is in the all-measures panel an
 * open item shows.
 *
 * **The band check lives inside each measure cell, not in a column of its own.**
 * An item-level "out of spec" count cannot say *which* measure drifted, and the
 * one that used to be here disagreed with TraceGains' own verdict three times
 * out of four without being able to explain why. Each cell now marks its own
 * lots and names the band it judged them against; `tg_fails` stays as a single
 * column because it is a different question — TraceGains' verdict at receipt,
 * not this item's current specification.
 *
 * Every statistic is the server's. `n` sits under each average because the
 * measures are sparse: an average over 3 lots and one over 40 are otherwise
 * indistinguishable.
 */

import { Fragment, useState } from 'react'

import type { CustomReportProps } from '..'
import { Card, Empty } from '../shared/Card'
import { displayText, formatDate } from '../shared/format'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

interface Measure {
  /** Column key prefix, as the manifest declares it. */
  readonly key: string
  /** Full name, for the all-measures panel and the lot table. */
  readonly label: string
  /** Column header on the item table, where width is scarce. */
  readonly short: string
  readonly decimals: number
  /** Whether Spec Management defines limits for it. */
  readonly banded: boolean
}

/** The six measures, in the order the manifest declares them. */
const MEASURES: readonly Measure[] = [
  { key: 'brix', label: 'Brix', short: 'Brix', decimals: 2, banded: true },
  { key: 'acid_citric', label: 'Acid', short: 'Acid', decimals: 3, banded: true },
  { key: 'brix_acid_ratio', label: 'Brix/Acid', short: 'BAR', decimals: 2, banded: true },
  { key: 'recoverable_oil', label: 'Recoverable Oil', short: 'Oil', decimals: 3, banded: true },
  { key: 'floating_pulp', label: 'Floating Pulp', short: 'Pulp', decimals: 2, banded: true },
  // No limits in Spec Management, here or on Finished Product.
  { key: 'usda_color', label: 'USDA Color', short: 'Color', decimals: 1, banded: false },
]

/** The five the item table gives a column: Brix, Acid, BAR, Oil, Pulp. */
const COLUMN_MEASURES = MEASURES.filter((measure) => measure.banded)

function num(value: unknown, decimals: number): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toFixed(decimals)
}

function gallons(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value ?? 0)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function isPresent(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

/** "11.00 – 13.50", "≥ 11.00", "≤ 13.50", or nothing at all. */
function band(low: unknown, high: unknown, decimals: number): string {
  const hasLow = isPresent(low)
  const hasHigh = isPresent(high)
  if (!hasLow && !hasHigh) return '—'
  if (hasLow && hasHigh) return `${num(low, decimals)} – ${num(high, decimals)}`
  return hasLow ? `≥ ${num(low, decimals)}` : `≤ ${num(high, decimals)}`
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span className={`chevron${open ? ' is-open' : ''}`} aria-hidden="true">
      <svg width="10" height="10" viewBox="0 0 10 10">
        <path d="M3 1 L7 5 L3 9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    </span>
  )
}

/** A lot's verdict, coloured by severity. FAIL is the one a reader must see. */
function SpecFlag({ flag }: { flag: string | null }) {
  if (flag === null) return <span>—</span>
  const role = flag === 'FAIL' ? 'is-fail' : flag === 'WARN' ? 'is-warn' : 'is-ok'
  return <span className={`is-flag ${role}`}>{flag}</span>
}

/**
 * One measure for one item: the average, with its range and `n` beneath.
 *
 * The range is the subscript rather than a pair of columns because it is
 * context for the average, not a number a reader scans down a column. Lots
 * outside the band are called out here — under the measure that they breached —
 * and the cell's title carries the band itself.
 */
function MeasureCell({ row, measure }: { row: Keyed; measure: Measure }) {
  const average = row[`${measure.key}_avg`]
  const n = count(row[`${measure.key}_n`])
  const out = count(row[`${measure.key}_out`])
  const low = row[`${measure.key}_low`]
  const high = row[`${measure.key}_high`]
  const hasBand = isPresent(low) || isPresent(high)

  if (!isPresent(average)) {
    return (
      <td className="cell-number is-measure-cell">
        <span className="is-measure-avg is-muted">—</span>
        <span className="is-measure-sub">not measured</span>
      </td>
    )
  }

  const bandText = hasBand ? `Spec ${band(low, high, measure.decimals)}` : 'No specification'
  const outText = out > 0 ? ` · ${out.toLocaleString()} of ${n.toLocaleString()} lots outside` : ''

  return (
    <td className="cell-number is-measure-cell" title={`${bandText}${outText}`}>
      <span className="is-measure-avg">{num(average, measure.decimals)}</span>
      <span className="is-measure-sub">
        {num(row[`${measure.key}_min`], measure.decimals)}–
        {num(row[`${measure.key}_max`], measure.decimals)}
        <span className="is-measure-n"> n {n.toLocaleString()}</span>
      </span>
      {out > 0 && (
        <span className="is-measure-out">{out.toLocaleString()} out</span>
      )}
    </td>
  )
}

export function ItemSpecs({ result }: CustomReportProps) {
  const items = dataset(result, 'items')
  const lots = dataset(result, 'lots')
  const loads = dataset(result, 'loads')

  const [openItems, setOpenItems] = useState<Set<string>>(new Set())
  const [openLots, setOpenLots] = useState<Set<string>>(new Set())

  const toggle = (set: Set<string>, key: string, apply: (next: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    apply(next)
  }

  const totalLots = items.reduce((sum, row) => sum + count(row.lots), 0)
  const totalGallons = items.reduce((sum, row) => sum + count(row.gallons), 0)
  const withSpec = items.filter((row) => text(row.has_spec) === 'true').length

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {items.length.toLocaleString()} {items.length === 1 ? 'item' : 'items'} over{' '}
        {totalLots.toLocaleString()} matched {totalLots === 1 ? 'lot' : 'lots'} and{' '}
        {gallons(totalGallons)} gallons. {withSpec.toLocaleString()} of{' '}
        {items.length.toLocaleString()} have an approved specification; averages are over lots,
        not loads.
      </p>

      <Card
        title="Juice items"
        subtitle={
          'Each measure shows the average over lots, with its range and lot count beneath. ' +
          'A cell marked "n out" has that many lots outside the item’s own spec band — hover ' +
          'it for the band. "TG fails" is a different question: lots TraceGains itself judged ' +
          'Out Spec at receipt, which often disagrees with the current specification.'
        }
      >
        {items.length === 0 ? (
          <Empty label="No matched juice items for these filters." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="detail-table is-item-table">
              <thead>
                <tr>
                  <th aria-label="Expand" />
                  <th>SKU</th>
                  <th>Description</th>
                  <th className="cell-number">Lots</th>
                  <th className="cell-number">Loads</th>
                  <th className="cell-number">Gallons</th>
                  {COLUMN_MEASURES.map((measure) => (
                    <th className="cell-number" key={measure.key} title={measure.label}>
                      {measure.short}
                    </th>
                  ))}
                  <th className="cell-number" title="Lots TraceGains judged Out Spec at receipt">
                    TG fails
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const sku = displayText(text(row.sku))
                  const isOpen = openItems.has(sku)
                  const itemLots = lots.filter((lot) => text(lot.sku) === text(row.sku))
                  const fails = count(row.lots_out_of_spec)
                  return (
                    <Fragment key={sku}>
                      <tr
                        className={`detail-row is-item-row${isOpen ? ' is-open' : ''}`}
                        onClick={() => {
                          toggle(openItems, sku, setOpenItems)
                        }}
                      >
                        <td className="chevron-cell">
                          <button
                            type="button"
                            className="link-button"
                            aria-expanded={isOpen}
                            aria-label={`${isOpen ? 'Collapse' : 'Expand'} item ${sku}`}
                            onClick={(event) => {
                              event.stopPropagation()
                              toggle(openItems, sku, setOpenItems)
                            }}
                          >
                            <Chevron open={isOpen} />
                          </button>
                        </td>
                        <td className="cell-mono">{sku}</td>
                        <td className="is-description">{displayText(text(row.skudesc))}</td>
                        <td className="cell-number">{count(row.lots).toLocaleString()}</td>
                        <td className="cell-number">{count(row.loads).toLocaleString()}</td>
                        <td className="cell-number">{gallons(row.gallons)}</td>
                        {COLUMN_MEASURES.map((measure) => (
                          <MeasureCell row={row} measure={measure} key={measure.key} />
                        ))}
                        <td className={fails > 0 ? 'cell-number is-exception' : 'cell-number'}>
                          {fails.toLocaleString()}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td className="expanded-cell" colSpan={7 + COLUMN_MEASURES.length}>
                            <MeasureSummary row={row} />
                            <LotTable
                              lots={itemLots}
                              loads={loads}
                              openLots={openLots}
                              onToggle={(key) => {
                                toggle(openLots, key, setOpenLots)
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * All six measures for one item — including USDA colour, which has no column.
 *
 * This is the "specs and averages of the item overall" view: the band, the
 * target where one exists, and how many lots fell outside.
 */
function MeasureSummary({ row }: { row: Keyed }) {
  return (
    <div className="is-measure-block">
      <h4 className="is-measure-title">All measures — averaged over lots</h4>
      <table className="detail-table is-measure-table">
        <thead>
          <tr>
            <th>Measure</th>
            <th className="cell-number">n</th>
            <th className="cell-number">Average</th>
            <th className="cell-number">Min</th>
            <th className="cell-number">Max</th>
            <th className="cell-number">Spec</th>
            <th className="cell-number">Target</th>
            <th className="cell-number">Lots out</th>
          </tr>
        </thead>
        <tbody>
          {MEASURES.map((measure) => {
            const out = count(row[`${measure.key}_out`])
            return (
              <tr key={measure.key} className="detail-row">
                <td>{measure.label}</td>
                <td className="cell-number is-muted">
                  {count(row[`${measure.key}_n`]).toLocaleString()}
                </td>
                <td className="cell-number">{num(row[`${measure.key}_avg`], measure.decimals)}</td>
                <td className="cell-number is-muted">
                  {num(row[`${measure.key}_min`], measure.decimals)}
                </td>
                <td className="cell-number is-muted">
                  {num(row[`${measure.key}_max`], measure.decimals)}
                </td>
                <td className="cell-number is-muted">
                  {measure.banded
                    ? band(row[`${measure.key}_low`], row[`${measure.key}_high`], measure.decimals)
                    : '—'}
                </td>
                <td className="cell-number is-muted">
                  {num(row[`${measure.key}_target`], measure.decimals)}
                </td>
                <td className={out > 0 ? 'cell-number is-exception' : 'cell-number'}>
                  {measure.banded ? out.toLocaleString() : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function LotTable({
  lots,
  loads,
  openLots,
  onToggle,
}: {
  lots: Keyed[]
  loads: Keyed[]
  openLots: Set<string>
  onToggle: (key: string) => void
}) {
  if (lots.length === 0) return <Empty label="No lots for this item under the current filters." />

  return (
    <div className="is-lot-block">
      <h4 className="is-measure-title">
        {lots.length.toLocaleString()} {lots.length === 1 ? 'lot' : 'lots'}
      </h4>
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table is-lot-table">
          <thead>
            <tr>
              <th aria-label="Expand" />
              <th>Lot</th>
              <th>Source</th>
              <th>Flag</th>
              <th>Status</th>
              <th>Disposition</th>
              {MEASURES.map((measure) => (
                <th className="cell-number" key={measure.key} title={measure.label}>
                  {measure.short}
                </th>
              ))}
              <th className="cell-number">Loads</th>
              <th className="cell-number">Gallons</th>
              <th>Measured</th>
            </tr>
          </thead>
          <tbody>
            {lots.map((lot) => {
              const sku = displayText(text(lot.sku))
              const lotId = displayText(text(lot.lot))
              const key = `${sku}::${lotId}`
              const isOpen = openLots.has(key)
              const behind = loads.filter(
                (load) => text(load.sku) === text(lot.sku) && text(load.lot) === text(lot.lot),
              )
              return (
                <Fragment key={key}>
                  <tr
                    className={`detail-row is-lot-row${isOpen ? ' is-open' : ''}`}
                    onClick={() => {
                      onToggle(key)
                    }}
                  >
                    <td className="chevron-cell">
                      <button
                        type="button"
                        className="link-button"
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} lot ${lotId}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          onToggle(key)
                        }}
                      >
                        <Chevron open={isOpen} />
                      </button>
                    </td>
                    <td className="cell-mono">{lotId}</td>
                    <td>{displayText(text(lot.tg_source))}</td>
                    <td>
                      <SpecFlag flag={text(lot.spec_flag)} />
                    </td>
                    <td>{displayText(text(lot.spec_status))}</td>
                    <td>{displayText(text(lot.disposition))}</td>
                    {MEASURES.map((measure) => (
                      <td className="cell-number" key={measure.key}>
                        {num(lot[measure.key], measure.decimals)}
                      </td>
                    ))}
                    <td className="cell-number">{count(lot.loads).toLocaleString()}</td>
                    <td className="cell-number">{gallons(lot.gallons)}</td>
                    <td>{formatDate(text(lot.measured_at))}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td className="expanded-cell" colSpan={12 + MEASURES.length}>
                        <LoadTable loads={behind} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LoadTable({ loads }: { loads: Keyed[] }) {
  if (loads.length === 0) return <Empty label="No loads behind this lot." />

  return (
    <table className="detail-table is-load-table">
      <thead>
        <tr>
          <th>Load ID</th>
          <th>Plant</th>
          <th>WMS status</th>
          <th>Location</th>
          <th>Area</th>
          <th className="cell-number">Units</th>
          <th className="cell-number">Available</th>
          <th className="cell-number">On hold</th>
          <th>Received</th>
          <th>Expires</th>
        </tr>
      </thead>
      <tbody>
        {loads.map((load) => (
          <tr key={displayText(text(load.loadid))} className="detail-row">
            <td className="cell-mono">{displayText(text(load.loadid))}</td>
            <td>{displayText(text(load.plant))}</td>
            <td>{displayText(text(load.wms_status))}</td>
            <td className="cell-mono">{displayText(text(load.location))}</td>
            <td>{displayText(text(load.warehousearea))}</td>
            <td className="cell-number">{gallons(load.units)}</td>
            <td className="cell-number">{gallons(load.units_available)}</td>
            <td className="cell-number">{gallons(load.units_onhold)}</td>
            <td>{formatDate(text(load.receivedate))}</td>
            <td>{formatDate(text(load.expirationdate))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
